window.ABWComboBox = (function() {
    'use strict';

    // Constants
    const CLASSES = {
        SELECT: 'abw-combobox-select',
        WRAPPER: 'abw-combobox-wrapper',
        INPUT: 'abw-combobox-input',
        TOGGLE: 'abw-combobox-toggle',
        CLEAR: 'abw-combobox-clear',
        LISTBOX: 'abw-combobox-listbox',
        OPTION: 'abw-combobox-option',
        NO_RESULTS: 'abw-combobox-no-results',
        VISIBLE: 'visible'
    };

    const ARIA = {
        EXPANDED: 'aria-expanded',
        HIDDEN: 'aria-hidden',
        SELECTED: 'aria-selected',
        ACTIVEDESCENDANT: 'aria-activedescendant',
        AUTOCOMPLETE: 'aria-autocomplete',
        CONTROLS: 'aria-controls',
        HASPOPUP: 'aria-haspopup',
        LABEL: 'aria-label'
    };

    const ATTRS = {
        DATA_VALUE: 'data-value',
        DATA_HIGHLIGHTED: 'data-highlighted'
    };

    const BLUR_TIMEOUT_MS = 150;
    const DEBOUNCE_MS = 150;

    // Helper function to debounce function calls
    function debounce(func, wait) {
        let timeout;
        return function(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    // Helper function to set multiple attributes
    function setAttributes(element, attrs) {
        Object.entries(attrs).forEach(([key, value]) => {
            element.setAttribute(key, value);
        });
    }

    // Helper function to create elements with class and attributes
    function createElement(tag, className = '', attributes = {}) {
        const el = document.createElement(tag);
        if (className) el.className = className;

        Object.entries(attributes).forEach(([key, value]) => {
            if (key === 'innerHTML') {
                el.innerHTML = value;
            } else if (key === 'textContent') {
                el.textContent = value;
            } else {
                el.setAttribute(key, value);
            }
        });

        return el;
    }

    function initCombobox(selectElement, inputClasses = null, wrapperClasses = null) {
        // Store all options from the original select
        const options = Array.from(selectElement.options)
            .filter(opt => opt.value !== '') // Exclude empty value options
            .map(opt => ({
                value: opt.value,
                text: opt.textContent,
                selected: opt.selected
            }));

        // Get the placeholder text from the first option with empty value
        const placeholderOption = Array.from(selectElement.options).find(opt => opt.value === '');
        const placeholderText = placeholderOption ? placeholderOption.textContent : 'Select an option';

        // Create combobox wrapper
        const wrapper = createElement('div', CLASSES.WRAPPER);

        // Add additional classes to wrapper if provided
        if (wrapperClasses) {
            const classes = Array.isArray(wrapperClasses) ? wrapperClasses : wrapperClasses.split(' ');
            classes.forEach(cls => {
                if (cls.trim()) wrapper.classList.add(cls.trim());
            });
        }

        // Create input
        const input = createElement('input', CLASSES.INPUT, {
            'type': 'text',
            'role': 'combobox',
            [ARIA.AUTOCOMPLETE]: 'list',
            [ARIA.EXPANDED]: 'false',
            [ARIA.CONTROLS]: `abw-combobox-listbox-${selectElement.id}`,
            [ARIA.HASPOPUP]: 'listbox',
            'autocomplete': 'off',
            'id': `abw-combobox-input-${selectElement.id}`,
            'placeholder': placeholderText
        });

        // Add additional classes to input if provided
        if (inputClasses) {
            const classes = Array.isArray(inputClasses) ? inputClasses : inputClasses.split(' ');
            classes.forEach(cls => {
                if (cls.trim()) input.classList.add(cls.trim());
            });
        }

        // Create toggle icon
        const toggle = createElement('span', CLASSES.TOGGLE, {
            'innerHTML': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>'
        });
        toggle.style.pointerEvents = 'auto';
        toggle.style.cursor = 'pointer';

        // Create clear button
        const clearBtn = createElement('span', CLASSES.CLEAR, {
            'innerHTML': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>',
            'role': 'button',
            [ARIA.LABEL]: 'Clear selection',
            'tabindex': '-1'
        });

        // Create listbox
        const listbox = createElement('ul', CLASSES.LISTBOX, {
            'id': `abw-combobox-listbox-${selectElement.id}`,
            'role': 'listbox',
            [ARIA.HIDDEN]: 'true'
        });

        // State
        const state = {
            isOpen: false,
            highlightIndex: -1,
            filtered: [...options]
        };

        // Handle listbox clicks with event delegation
        listbox.addEventListener('click', (e) => {
            const optionEl = e.target.closest('.' + CLASSES.OPTION);
            if (optionEl) {
                const value = optionEl.getAttribute(ATTRS.DATA_VALUE);
                const option = state.filtered.find(opt => opt.value === value);
                if (option) {
                    selectOption(option);
                }
            }
        });

        // Filter options based on search term
        function filterOptions(searchTerm) {
            return searchTerm
                ? options.filter(opt => opt.text.toLowerCase().includes(searchTerm.toLowerCase()))
                : [...options];
        }

        // Build listbox options
        function renderOptions(filter = '') {
            listbox.innerHTML = '';
            state.filtered = filterOptions(filter);

            if (state.filtered.length === 0) {
                const noResults = createElement('li', CLASSES.NO_RESULTS, {
                    'textContent': 'No matches found'
                });
                listbox.appendChild(noResults);
                return;
            }

            const fragment = document.createDocumentFragment();
            state.filtered.forEach((opt, index) => {
                const li = createElement('li', CLASSES.OPTION, {
                    'role': 'option',
                    [ATTRS.DATA_VALUE]: opt.value,
                    'id': `abw-combobox-option-${selectElement.id}-${index}`,
                    'textContent': opt.text
                });
                fragment.appendChild(li);
            });
            listbox.appendChild(fragment);
            state.highlightIndex = -1;
        }

        // Open/close listbox
        function setListboxOpen(open) {
            if (state.isOpen === open) return;

            state.isOpen = open;
            setAttributes(listbox, { [ARIA.HIDDEN]: String(!open) });
            setAttributes(input, { [ARIA.EXPANDED]: String(open) });

            if (open) {
                renderOptions(input.value);
            } else {
                state.highlightIndex = -1;
                updateHighlight();
            }
        }

        function openListbox() {
            setListboxOpen(true);
        }

        function closeListbox() {
            setListboxOpen(false);
        }

        // Trigger change event on original select
        function dispatchChangeEvent() {
            selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Update selection
        function updateSelection(value = '', text = '') {
            selectElement.value = value;
            input.value = text;
            closeListbox();
            updateClearButton();
            dispatchChangeEvent();
        }

        // Select an option
        function selectOption(option) {
            updateSelection(option.value, option.text);
        }

        // Clear selection
        function clearSelection() {
            updateSelection();
        }

        // Update clear button visibility
        function updateClearButton() {
            clearBtn.classList.toggle(CLASSES.VISIBLE, !!input.value);
        }

        // Highlight management
        function updateHighlight() {
            if (state.highlightIndex < 0) {
                input.removeAttribute(ARIA.ACTIVEDESCENDANT);
                return;
            }

            const optionElements = listbox.querySelectorAll('.' + CLASSES.OPTION);
            optionElements.forEach((el, index) => {
                el.removeAttribute(ATTRS.DATA_HIGHLIGHTED);
                setAttributes(el, { [ARIA.SELECTED]: 'false' });
                if (index === state.highlightIndex) {
                    setAttributes(el, {
                        [ATTRS.DATA_HIGHLIGHTED]: 'true',
                        [ARIA.SELECTED]: 'true'
                    });
                    el.scrollIntoView({ block: 'nearest' });
                    setAttributes(input, { [ARIA.ACTIVEDESCENDANT]: el.id });
                }
            });
        }

        function moveHighlight(direction) {
            const newIndex = state.highlightIndex + direction;
            if (newIndex >= 0 && newIndex < state.filtered.length) {
                state.highlightIndex = newIndex;
                updateHighlight();
            }
        }

        // Event listeners
        const debouncedRender = debounce((value) => renderOptions(value), DEBOUNCE_MS);

        input.addEventListener('input', (e) => {
            updateClearButton();
            if (!state.isOpen) {
                openListbox();
            } else {
                debouncedRender(e.target.value);
            }
        });

        input.addEventListener('click', () => {
            if (!state.isOpen) {
                openListbox();
            }
        });

        // Keyboard handlers
        const keyHandlers = {
            'ArrowDown': (e) => {
                e.preventDefault();
                state.isOpen ? moveHighlight(1) : openListbox();
            },
            'ArrowUp': (e) => {
                e.preventDefault();
                if (state.isOpen) moveHighlight(-1);
            },
            'Enter': (e) => {
                e.preventDefault();
                if (state.isOpen && state.highlightIndex >= 0) {
                    selectOption(state.filtered[state.highlightIndex]);
                }
            },
            'Escape': (e) => {
                if (state.isOpen) {
                    e.preventDefault();
                    closeListbox();
                }
            },
            'Tab': (e) => {
                if (state.isOpen) {
                    e.preventDefault();
                    if (state.highlightIndex >= 0) {
                        selectOption(state.filtered[state.highlightIndex]);
                    }
                }
                closeListbox();
            }
        };

        input.addEventListener('keydown', (e) => {
            const handler = keyHandlers[e.key];
            if (handler) handler(e);
        });

        // Clear button click
        clearBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            clearSelection();
            input.focus();
        });

        // Toggle icon click
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            if (state.isOpen) {
                closeListbox();
            } else {
                openListbox();
            }
            input.focus();
        });

        // Blur event - clear if no match
        input.addEventListener('blur', (e) => {
            // Use setTimeout to allow click events on options to complete
            setTimeout(() => {
                const currentValue = input.value.trim();

                if (currentValue) {
                    // Check if current value exactly matches any option
                    const matchingOption = options.find(opt =>
                        opt.text.toLowerCase() === currentValue.toLowerCase()
                    );

                    if (!matchingOption) {
                        // No match found, reset to default state
                        clearSelection();
                    }
                }
            }, BLUR_TIMEOUT_MS);
        });

        // Click outside to close
        const handleClickOutside = (e) => {
            if (!wrapper.contains(e.target)) {
                closeListbox();
            }
        };
        document.addEventListener('click', handleClickOutside);

        // Assemble and replace
        wrapper.appendChild(input);
        wrapper.appendChild(clearBtn);
        wrapper.appendChild(toggle);
        wrapper.appendChild(listbox);

        selectElement.style.display = 'none';
        selectElement.parentNode.insertBefore(wrapper, selectElement);

        // Set initial value
        const selectedOption = options.find(opt => opt.selected && opt.value);
        if (selectedOption) {
            input.value = selectedOption.text;
        }
        updateClearButton();

        // Return cleanup function; can be used by calling code to clean up event listeners if comboxes are
        // dynamically added/removed on a page
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }

    // Public API
    return {
        /**
         * Initialize combobox on select element(s)
         * @param {string|HTMLSelectElement|NodeList} target - CSS selector, single element, or NodeList of select elements
         * @param {Object} options - Configuration options
         * @param {string|string[]} options.inputClasses - Additional class name(s) to add to the input element
         * @param {string|string[]} options.wrapperClasses - Additional class name(s) to add to the wrapper element
         * @returns {Function|Function[]} Cleanup function(s)
         */
        init: function(target, options = {}) {
            let elements;

            if (typeof target === 'string') {
                // CSS selector string
                elements = document.querySelectorAll(target);
            } else if (target instanceof HTMLSelectElement) {
                // Single element
                elements = [target];
            } else if (target instanceof NodeList || Array.isArray(target)) {
                // NodeList or array
                elements = target;
            } else {
                console.error('ABWComboBox.init: Invalid target. Expected string selector, HTMLSelectElement, or NodeList.');
                return null;
            }

            const cleanupFunctions = Array.from(elements).map(el =>
                initCombobox(el, options.inputClasses, options.wrapperClasses)
            );
            return cleanupFunctions.length === 1 ? cleanupFunctions[0] : cleanupFunctions;
        },

        /**
         * Initialize all selects with the default class
         * @returns {Function[]} Array of cleanup functions
         */
        initAll: function() {
            return this.init(`select.${CLASSES.SELECT}`);
        }
    };
})();
